import { Component, h } from 'preact'
import Card from '../cards/index'
import Phrase from '../../libs/translationsUtils/phrase'
import { GetServerSideProps } from '../../server-middleware/context'
import {
  getCategories,
  LegacyCategoryType,
  prepCategories
} from '../../server-middleware/categoryRepository'
import { z } from 'zod'
import { CategoryInfo, ParentCategoryInfo } from '../mega-menu/Types'
import { textToKebab } from '../utils/toKebabCase'

interface Props {
  title?: string
  selectedTemplate: number
  displayOnlyParent: boolean
  parentCategories: ParentCategoryInfo[]
}

export const widgetName = 'community_category' as const

export const config = z.object({
  title: z.string().optional(),
  selectedTemplate: z.number(),
  displayOnlyParent: z.boolean()
})

type Config = z.infer<typeof config>

export const getServerSideProps: GetServerSideProps<Config, Props> = async (ctx, config) => {
  return await ctx.cache(`community-category_${JSON.stringify(config)}_${ctx.userId}`, async () => {
    const list = await prepCategories(
      ctx,
      await getCategories(ctx, [
        LegacyCategoryType.default,
        LegacyCategoryType.news,
        LegacyCategoryType.ideation
      ])
    )

    return {
      title: config.title,
      selectedTemplate: config.selectedTemplate,
      displayOnlyParent: config.displayOnlyParent,
      parentCategories: list
    }
  })
}

class CommunityCategoryWrapper extends Component<Props> {
  constructor(props: Props) {
    super(props)
    this.getMetadata = this.getMetadata.bind(this)
  }

  templates = ['big-card', 'small-card']

  getMetadata(categories: CategoryInfo[]) {
    let totalTopicCount = 0
    let totalReplyCount = 0

    categories.forEach((category) => {
      totalTopicCount += category.topicCount
      totalReplyCount += category.replyCount
    })

    return (
      <ul class='list--dot-seperated community-category__card-metadata'>
        <li class='qa-forum-overview-topic-counter'>
          <Phrase
            m='Forum'
            p='forumList.meta.counter.topic'
            count={{ n: totalTopicCount.toString() }}
          />
        </li>
        <li class='qa-forum-overview-post-counter'>
          <Phrase
            m='Forum'
            p='forumList.meta.counter.post'
            count={{ n: totalReplyCount.toString() }}
          />
        </li>
      </ul>
    )
  }

  renderOnlyParent() {
    const { selectedTemplate, parentCategories } = this.props
    return parentCategories.map((item: ParentCategoryInfo, idx) => {
      const metadata = this.getMetadata(item.subforums)
      const kebabCasedTitle = textToKebab(item.title)
      return (
        <Card
          customClass={`community-category__card community-category__card-${this.templates[selectedTemplate]} id-${item.id} title-${kebabCasedTitle}`}
          idx={idx}
          key={item.id}
          title={item.title}
          metadata={metadata}
          imageSrc={item.thumbnailUrl}
          navigation={{
            url: item.url
          }}
        />
      )
    })
  }

  renderWithChilds() {
    const { title, selectedTemplate, parentCategories } = this.props
    return (
      <div className='community-category-list_parent-child-component-wrapper'>
        {parentCategories.map((category) => (
          <section className='community-category-list_parent-child-component' key={category.id}>
            {title
              ? (
                <div className='community-category-list_parent-title'>{category.title}</div>
                )
              : (
                <h3 className='widget__heading'>{category.title}</h3>
                )}
            <div className='community-category-list'>
              {category.subforums.map((subcategory, idx) => {
                const kebabCasedTitle = textToKebab(subcategory.title)
                return (
                  <Card
                    key={subcategory.id}
                    customClass={`community-category__card community-category__card-${this.templates[selectedTemplate]} id-${subcategory.id} title-${kebabCasedTitle}`}
                    idx={idx}
                    title={subcategory.title}
                    metadata={<Metadata topicCount={subcategory.topicCount} />}
                    imageSrc={subcategory.thumbnailUrl}
                    navigation={{
                      url: subcategory.url
                    }}
                  />
                )
              })}
            </div>
          </section>
        ))}
      </div>
    )
  }

  render({ title, displayOnlyParent }: Props) {
    return (
      <section className='full-width Template-brand-featured qa-community-category-container community-category-container homepage-widget-wrapper'>
        <div class='sitewidth'>
          <div class='col'>
            {Boolean(title) && <h3 className='widget__heading'>{title}</h3>}
            <div className='community-category-list'>
              {displayOnlyParent ? this.renderOnlyParent() : this.renderWithChilds()}
            </div>
          </div>
        </div>
      </section>
    )
  }
}

const Metadata = ({ topicCount }: { topicCount: number }) => (
  <div className='community__card-metadata'>
    <p className='text--meta'>
      <Phrase m='Forum' p='forumList.meta.counter.topic' count={{ n: topicCount.toString() }} />
    </p>
  </div>
)

export default CommunityCategoryWrapper
